import { ApplicationType } from '@application/types';
import { usePopper, useRouter } from '@application/utils';
import type {
  AccountGetBreederApplicationsQuery,
  AccountGetBuyerApplicationsQuery,
} from '@gql/graphql';
import type { ActionsPopperData } from '@presentation/components';
import { useCallback, useMemo, useState } from 'react';

type TBuyerApplicationsType =
  | NonNullable<AccountGetBuyerApplicationsQuery['accountMe']>['applications'][number];

type TBreederApplicationsType = NonNullable<
  NonNullable<AccountGetBreederApplicationsQuery['accountMe']>['dogBreeder']
>['applications'][number];

type TApplicationsType = Array<TBuyerApplicationsType | TBreederApplicationsType>;

export interface UseApplicationsSharedParams<TApplications extends TApplicationsType> {
  applications: TApplications;
  type?: ApplicationType;
}

export const useApplicationsShared = <TApplications extends TApplicationsType>(
  params: UseApplicationsSharedParams<TApplications>,
) => {
  const { applications, type } = params;
  const { translate, navigateToRoute } = useRouter();
  const popper = usePopper();
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | undefined>(undefined);

  const detailsClickHandler = useCallback(async () => {
    popper.close();
    if (!selectedApplicationId) return;
    if (!type || type === ApplicationType.APPLICATIONS) {
      navigateToRoute('mtApplications', { params: [selectedApplicationId.toString()] });
    } else if (type === ApplicationType.WAITING_LIST) {
      navigateToRoute('mtWaitingList', { params: [selectedApplicationId.toString()] });
    } else {
      navigateToRoute('mtOwners', { params: [selectedApplicationId.toString()] });
    }
  }, [navigateToRoute, popper, selectedApplicationId, type]);

  const selectedApplication: TBuyerApplicationsType | TBreederApplicationsType | undefined =
    useMemo(() => {
      if (!selectedApplicationId) return;
      return applications.find(({ id }) => id === selectedApplicationId);
    }, [applications, selectedApplicationId]);

  // XXX We can' add a shared message handler here as it is conditionally displayed
  const popperItems = useMemo((): ActionsPopperData['items'] => {
    return [
      {
        label: translate('common.buttons.details'),
        iconName: 'link',
        onClick: detailsClickHandler,
      },
    ];
  }, [detailsClickHandler, translate]);

  return {
    selectedApplication,
    setSelectedApplicationId,
    popperItems,
    popper,
  };
};
